import type Partner          from "@/domains/Partners/Partner";
import type Company          from "@/domains/Companies/Company";
import type Store            from "@/domains/Stores/Store";
import type ProductionSite   from "@/domains/ProductionSites/ProductionSite";
import type Person           from "@/domains/Persons/Person";
import type ContractExternal from "@/domains/Contracts/ContractExternal";
import type DateSimple       from "@/core/types/DateSimple";
import type DeliveryType     from "@/domains/DeliveryTypes/DeliveryType";
import type PaymentTerm      from "@/domains/PaymentTerms/PaymentTerm";
import type Tender           from "@/domains/Tenders/Tender";
import type TenderBid        from "@/domains/TenderBids/TenderBid";
import type OrderCategory    from "@/domains/Orders/OrderCategory";
import type Offer            from "@/domains/Offers/Offer";
import type DemandOption     from "@/domains/DemandsV2/DemandOption";
import type Qty              from "@/core/types/Qty";
import type PreorderPosition from "@/domains/Preorders/PreorderPosition";
import AdditionalFields, {field} from "@/core/misc/decorators/AdditionalFields";
import Title_ru_RU           from "@/core/misc/decorators/Title_ru_RU";
import SerializeSimplified   from "@/core/misc/decorators/SerializeSimplified";
import NoSerialize           from "@/core/misc/decorators/NoSerialize";
import TYPES                 from "@/core/services/Changelogs/types";
import Core                  from "@/core/Core";
import BaseEntity            from '@/core/misc/BaseEntity';
import OrderRequisites       from '@/domains/Orders/OrderRequisites';
import CurrenciesService     from "@/domains/Currencies/CurrenciesService";
import Currency              from "@/domains/Currencies/Currency";
import ContractPublic        from "@/domains/Contracts/ContractPublic";

@AdditionalFields({
  prepaymentTerm:          field('предоплата', TYPES.PAYMENT_TERM),
  postpaymentTerm:         field('постоплата', TYPES.PAYMENT_TERM),
  qtyComplainedSubstandard: field('количество некондиции по претензии', TYPES.NUMBER),
})
/**
 * Абстрактная сущность заказа — общий предок Order и OrderPosition.
 *
 * Реквизиты сделки (участники, договор, доставка, валюта, оплата, флаги)
 * вынесены в OrderRequisites. Заказ и его позиции делят ОДИН экземпляр
 * requisites по ссылке — рассинхрон конструктивно невозможен.
 */
abstract class AbstractOrderEntity extends BaseEntity {
  // Константы причин — общие для всех наследников
  static ReasonUnavailable = {
    IS_OFFER_BASED:   'Позиция заказа была создана на основе предложения',
    IS_DELETED:       'Позиция удалена',
    ALREADY_FINISHED: 'Позиция в списке завершенных',
    NO_ACCESS:        'Нет прав',
    IS_NOT_TENDER:    'Позиция создана не из тендера',
  } as const;

  /** Реквизиты сделки. Общий объект для заказа и его позиций. */
  @NoSerialize
  public requisites: OrderRequisites;

  // ---- Валюта ----
  protected _currencyCode: string | null;
  protected _currency: Currency | null;

  static fromServer(data: any, result?: AbstractOrderEntity): AbstractOrderEntity {
    if (result === undefined) {
      throw new Error('AbstractOrderEntity.fromServer вызывается только из наследника с готовым инстансом');
    }

    super.fromServer(data, result);

    result.requisites = OrderRequisites.fromServer(data);

    // ---- Валюта ----
    // super положил _currencyCode напрямую, минуя сеттер — _currency не пересчитан.
    // Прогоняем через сеттер, чтобы он подтянул _currency из словаря.
    result.currencyCode = result._currencyCode;

    return result;
  }

  static fromOffer(offer: Offer, result: AbstractOrderEntity): AbstractOrderEntity {
    result.requisites = OrderRequisites.fromOffer(offer);

    // ---- Валюта ----
    result.currencyCode = offer.offerCurrencyCode;

    return result;
  }

  static fromTenderBid(tenderBid: TenderBid, tender: Tender, result: AbstractOrderEntity): AbstractOrderEntity {
    result.requisites = OrderRequisites.fromTenderBid(tenderBid, tender);
    result.isPreorder = tender.isPreorderNeeded;

    // ---- Валюта ----
    result.currencyCode = tenderBid.offerCurrencyCode;

    return result;
  }

  static fromDemandV2Option(usedOption: { option: DemandOption, usedQty: Qty }, result: AbstractOrderEntity): AbstractOrderEntity {
    result.requisites = OrderRequisites.fromDemandV2Option(usedOption);

    // ---- Валюта ----
    // Допущение: для одного поставщика не заведут две DemandOption с разными currencyCode
    result.currencyCode = usedOption.option.offerCurrencyCode;

    return result;
  }

  static fromPreorderPosition(preorderPosition: PreorderPosition, result: AbstractOrderEntity): AbstractOrderEntity {
    result.requisites = OrderRequisites.fromPreorderPosition(preorderPosition);

    // ---- Валюта ----
    result.currencyCode = preorderPosition.currencyCode;

    return result;
  }

  toString(): string {
    const type   = (this.constructor as any).__entityTypeName;
    const number = this.formattedNumber ? `№${this.formattedNumber}` : '';
    const date   = this.createdAt ? `от ${this.createdAt.format()}` : '';

    return `${type} ${number} ${date}`.trim();
  }

  setOrder(order: AbstractOrderEntity): void {
    // Собственная валюта позиции сильнее валюты заказа: цена позиции названа в ней,
    // и перебивание кода валюты переименовало бы цену, не пересчитав её.
    if (Core.utils.isNullOrUndefined(this._currencyCode) === false) {
      // у позиции уже есть валюта — заказ её не трогает
    } else {
      if (Core.utils.isNullOrUndefined(order._currencyCode) === false) {
        this.currencyCode = order.currencyCode;
      } else {
        // Это важно сделать не просто для ускорения ввода, а ещё потому что бесплатные образцы тоже должны иметь валюту
        this.currencyCode = Core.displayedCurrencyCode;
      }
    }
  }

  get currencyCode(): string | null { return this._currencyCode; }
  set currencyCode(value: string | null) {
    this._currencyCode = value;

    if (this._currencyCode) {
      this._currency = CurrenciesService.currencies.get(this._currencyCode);
    } else {
      this._currency = null;
    }
  }

  get currency(): Currency | null { return this._currency; }
  set currency(value: Currency | null) {
    this._currency = value;
    this.currencyCode = this._currency?.code ?? null;
  }

  getReasonCannotNotify(): string | null {
    if (!this.requisites.isOfferBased && Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.IS_OFFER_BASED;
    }

    if (this.requisites.isFinished) {
      return AbstractOrderEntity.ReasonUnavailable.ALREADY_FINISHED;
    }

    if (this.isDeleted) {
      return AbstractOrderEntity.ReasonUnavailable.IS_DELETED;
    }

    return null;
  }

  getReasonCannotEditDates(): string | null {
    if (Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.NO_ACCESS;
    }

    if (!this.requisites.isOfferBased && Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.IS_OFFER_BASED;
    }

    if (this.requisites.isFinished) {
      return AbstractOrderEntity.ReasonUnavailable.ALREADY_FINISHED;
    }

    if (this.isDeleted) {
      return AbstractOrderEntity.ReasonUnavailable.IS_DELETED;
    }

    return null;
  }

  getReasonCannotDelete(): string | null {
    if (Core.currentPartner.isSeller === true) {
      return AbstractOrderEntity.ReasonUnavailable.NO_ACCESS;
    }

    if (!this.requisites.isOfferBased && Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.IS_OFFER_BASED;
    }

    if (this.requisites.isFinished) {
      return AbstractOrderEntity.ReasonUnavailable.ALREADY_FINISHED;
    }

    if (this.isDeleted) {
      return AbstractOrderEntity.ReasonUnavailable.IS_DELETED;
    }

    return null;
  }

  getReasonCannotClose(): string | null {
    if (Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.NO_ACCESS;
    }

    return null;
  }

  getReasonCannotUnconfirm(): string | null {
    if (!this.requisites.isOfferBased && Core.currentPartner.isSeller) {
      return AbstractOrderEntity.ReasonUnavailable.IS_OFFER_BASED;
    }

    if (this.requisites.isOfferBased) {
      return AbstractOrderEntity.ReasonUnavailable.IS_OFFER_BASED;
    }

    return null;
  }

  getReasonCannotCreateAssembly(): string | null {
    if (this.requisites.isFinished) {
      return AbstractOrderEntity.ReasonUnavailable.ALREADY_FINISHED;
    }

    if (this.isDeleted) {
      return AbstractOrderEntity.ReasonUnavailable.IS_DELETED;
    }

    return null;
  }

  getReasonCannotCreateShipment(): string | null {
    if (this.requisites.isFinished) {
      return AbstractOrderEntity.ReasonUnavailable.ALREADY_FINISHED;
    }

    if (this.isDeleted) {
      return AbstractOrderEntity.ReasonUnavailable.IS_DELETED;
    }

    return null;
  }

  getReasonCannotViewTenderHistory(): string | null {
    if (!this.requisites.tender?.id) {
      return AbstractOrderEntity.ReasonUnavailable.IS_NOT_TENDER;
    }

    return null;
  }

  static isDraftParser(oldValue, newValue) {
    let result = '';

    if (typeof newValue === 'boolean') {
      if (newValue === true) {
        result += ' - метка установлена';
      } else {
        result += ' - метка снята';
      }
    }
    return result;
  }

  // ==================== Прокси-доступ к реквизитам ====================
  // Делегаты к this.requisites в порядке объявления полей OrderRequisites.
  // Позволяют шаблонам и коду читать/писать order.seller вместо order.requisites.seller.
  // Валюта (currencyCode/currency) живёт на самой сущности — здесь её нет.

  get isOfferBased(): boolean { return this.requisites.isOfferBased; }
  set isOfferBased(value: boolean) { this.requisites.isOfferBased = value; }

  get isForeign(): boolean { return this.requisites.isForeign; }
  set isForeign(value: boolean) { this.requisites.isForeign = value; }

  get isPreorder(): boolean { return this.requisites.isPreorder; }
  set isPreorder(value: boolean) { this.requisites.isPreorder = value; }

  @Title_ru_RU('отметка завершённости', TYPES.BOOLEAN_MARK)
  get isFinished(): boolean { return this.requisites.isFinished; }
  set isFinished(value: boolean) { this.requisites.isFinished = value; }

  @Title_ru_RU('метка образцовости', TYPES.BOOLEAN_MARK)
  get isSamples(): boolean { return this.requisites.isSamples; }
  set isSamples(value: boolean) { this.requisites.isSamples = value; }

  @Title_ru_RU('метка образцы идут в производство', TYPES.BOOLEAN_MARK )
  get isForTestsOnly(): boolean | null { return this.requisites.isForTestsOnly; }
  set isForTestsOnly(value: boolean | null) { this.requisites.isForTestsOnly = value; }

  @SerializeSimplified
  get orderCategory(): OrderCategory { return this.requisites.orderCategory; }
  set orderCategory(value: OrderCategory) { this.requisites.orderCategory = value; }

  get isFreeSamples(): boolean { return this.requisites.isFreeSamples; }
  get isPrinting(): boolean { return this.requisites.isPrinting; }
  get isRawMaterialsAndPackage(): boolean { return this.requisites.isRawMaterialsAndPackage; }

  @SerializeSimplified
  get seller(): Partner { return this.requisites.seller; }
  set seller(value: Partner) { this.requisites.seller = value; }

  @Title_ru_RU('Юр. лицо', TYPES.OBJECT)
  @SerializeSimplified
  get sellerCompany(): Company { return this.requisites.sellerCompany; }
  set sellerCompany(value: Company) { this.requisites.sellerCompany = value; }

  @SerializeSimplified
  get sellerStore(): Store { return this.requisites.sellerStore; }
  set sellerStore(value: Store) { this.requisites.sellerStore = value; }

  @SerializeSimplified
  get buyer(): Partner { return this.requisites.buyer; }
  set buyer(value: Partner) { this.requisites.buyer = value; }

  @SerializeSimplified
  get buyerCompany(): Company { return this.requisites.buyerCompany; }
  set buyerCompany(value: Company) { this.requisites.buyerCompany = value; }

  @SerializeSimplified
  get buyerStore(): Store { return this.requisites.buyerStore; }
  set buyerStore(value: Store) { this.requisites.buyerStore = value; }

  @Title_ru_RU('производственная площадка', TYPES.OBJECT)
  @SerializeSimplified
  get buyerProductionSite(): ProductionSite | null { return this.requisites.buyerProductionSite; }
  set buyerProductionSite(value: ProductionSite | null) { this.requisites.buyerProductionSite = value; }

  @SerializeSimplified
  get responsibleSeller(): Person | null { return this.requisites.responsibleSeller; }
  set responsibleSeller(value: Person | null) { this.requisites.responsibleSeller = value; }

  @Title_ru_RU('ответственный от покупателя', TYPES.OBJECT)
  @SerializeSimplified
  get responsibleBuyer(): Person { return this.requisites.responsibleBuyer; }
  set responsibleBuyer(value: Person) { this.requisites.responsibleBuyer = value; }

  @NoSerialize
  get responsible(): Person | null { return this.requisites.responsible; }

  @SerializeSimplified
  get contract(): ContractExternal | ContractPublic | null | undefined { return this.requisites.contract; }
  set contract(value: ContractExternal | ContractPublic | null | undefined) { this.requisites.contract = value; }

  get requestDate(): DateSimple | null { return this.requisites.requestDate; }
  set requestDate(value: DateSimple | null) { this.requisites.requestDate = value; }

  @SerializeSimplified
  get deliveryType(): DeliveryType { return this.requisites.deliveryType; }
  set deliveryType(value: DeliveryType) { this.requisites.deliveryType = value; }

  get deliveryDays(): number { return this.requisites.deliveryDays; }
  set deliveryDays(value: number) { this.requisites.deliveryDays = value; }

  @Title_ru_RU('метка доставки за счёт покупателя', TYPES.BOOLEAN_MARK)
  get isDeliveryPaidByBuyer(): boolean { return this.requisites.isDeliveryPaidByBuyer; }
  set isDeliveryPaidByBuyer(value: boolean) { this.requisites.isDeliveryPaidByBuyer = value; }

  get isVatPresented(): boolean { return this.requisites.isVatPresented; }

  @Title_ru_RU('НДС', TYPES.BOOLEAN, (oldValue, newValue) => `с ${oldValue ? 'включительно' : 'сверху'} на ${newValue ? 'включительно' : 'сверху'}`)
  get isVatIncludedSeller(): boolean | null { return this.requisites.isVatIncludedSeller; }
  set isVatIncludedSeller(value: boolean | null) { this.requisites.isVatIncludedSeller = value; }

  @Title_ru_RU('условия оплаты', TYPES.PAYMENT_TERM)
  get paymentTerms(): PaymentTerm[] { return this.requisites.paymentTerms; }
  set paymentTerms(value: PaymentTerm[]) { this.requisites.paymentTerms = value;}

  get postpaymentTerms(): PaymentTerm[] { return this.requisites.paymentTerms.filter(paymentTerm => paymentTerm.event?.paymentType === 'post'); }
  get prepaymentTerms(): PaymentTerm[] { return this.requisites.paymentTerms.filter(paymentTerm => paymentTerm.event?.paymentType === 'pre'); }

  @SerializeSimplified
  get tender(): Tender | null { return this.requisites.tender; }
  set tender(value: Tender | null) { this.requisites.tender = value; }

  @SerializeSimplified
  get tenderBid(): TenderBid | null { return this.requisites.tenderBid; }
  set tenderBid(value: TenderBid | null) { this.requisites.tenderBid = value; }
}

export default AbstractOrderEntity;
